import * as config from "../../../config";
import * as utils from "../../../utils";
import * as errorActions from "../error/errorActions";
import * as persistenceActions from "../persistence/persistenceActions";
import * as types from "./peersActionTypes";
import * as actions from "./peersActions";
import * as Sentry from "@sentry/react";
import * as logger from "common/logger";
import * as submissionsService from "services/submissionsService";
import * as programSelectors from "store/selectors/input/program/programSelectors";

export const updateChosenExposureMetric = (metric) => ({
  type: types.UPDATE_CHOSEN_PEER_EXPOSURE_METRIC,
  payload: metric,
});

export const updateChosenExposureMetric2 = (metric) => ({
  type: types.UPDATE_CHOSEN_PEER_EXPOSURE_METRIC_2,
  payload: metric,
});

export const receivedSubmission = (data) => ({
  type: types.LOAD_PEER,
  payload: persistenceActions.upgradeState(data),
});

export const updatePricingSelection = (
  peerFilename,
  layerIndex,
  weighting
) => ({
  type: types.UPDATE_SELECTED_PEERS,
  payload: {
    filename: peerFilename,
    layerIndex,
    weighting,
  },
});

export const updatePeerExposure = (chosenMetric) => ({
  type: types.UPDATE_PEER_CHOSEN_EXPOSURE_METRIC,
  payload: chosenMetric,
});

export const failedToLoadSubmission = (filename, error) => {
  Sentry.addBreadcrumb(`Failed to load: ${filename}`);
  Sentry.captureException(error);
  return {
    type: types.LOAD_PEER_FAILED,
    payload: filename,
  };
};

export const loadPeer = (peer) => (dispatch) => {
  dispatch({
    type: types.ADD_PEER,
    payload: peer,
  });
  return utils
    .authenticatedFetch(
      config.endpoints.persistence(
        "get?" + new URLSearchParams({ filename: peer.filename || peer.guid })
      )
    )
    .then((result) => result.json())
    .then((data) => {
      dispatch(actions.receivedSubmission(data));
    })
    .catch((error) => {
      dispatch(actions.failedToLoadSubmission(peer.filename, error));
    });
};

export const replacePeerSubmissions = (submissions) => ({
  type: types.REPLACE_PEER_SUBMISSIONS,
  payload: submissions,
});

// Walk a peer's renewal chain to its end. The backend's renewalMap lists only
// direct renewals, so follow each renewal's own renewals (2024 -> 2025 -> 2026)
// and pick the newest submission at the end of a chain (one with no renewals
// of its own); if none of those has an inception, fall back to any renewal in
// the chain.
const findLatestRenewal = (peerId, renewalMap, submissions) => {
  const visited = new Set([peerId]);
  const queue = [...(renewalMap?.[peerId]?.renewals || [])];
  const ends = [];
  const all = [];
  while (queue.length) {
    const id = queue.shift();
    if (visited.has(id)) continue;
    visited.add(id);
    const children = renewalMap?.[id]?.renewals || [];
    queue.push(...children);
    const sub = submissions?.[id];
    if (!sub?.inception) continue;
    all.push(id);
    if (!children.length) {
      ends.push(id);
    }
  }
  const newest = (ids) =>
    ids.reduce(
      (best, id) =>
        !best ||
        new Date(submissions[id].inception) >
          new Date(submissions[best].inception)
          ? id
          : best,
      null
    );
  return newest(ends) || newest(all);
};

const normalizeId = (f) =>
  f ? (f.endsWith(".json") ? f.slice(0, -5) : f) : "";

// Swap each peer to the newest renewal at the end of its chain.
// - A swapped peer's `pricing` (layer weightings) is dropped: weightings are
//   positional against the old tower, so carrying them would silently tick
//   different layers on the new one. The UW re-selects layers on the renewal.
// - A renewal that is the submission being edited is skipped (the peer is
//   left as it was), so our own file never appears as its own peer.
// - Peers that resolve to the same file are de-duplicated (first one wins).
const resolvePeersToRenewals = (
  peers,
  renewalMap,
  submissions,
  currentSubmissionId
) => {
  const seen = new Set();
  const resolved = [];
  peers.forEach((peer) => {
    const latest = findLatestRenewal(
      normalizeId(peer.filename),
      renewalMap,
      submissions
    );
    let next;
    if (!latest || latest === currentSubmissionId) {
      next = { ...peer };
    } else {
      // eslint-disable-next-line no-unused-vars
      const { pricing, ...rest } = peer;
      next = {
        ...rest,
        filename: `${latest}.json`,
        inception: submissions[latest].inception,
        insured: submissions[latest].insured ?? peer.insured,
      };
    }
    const key = normalizeId(next.filename);
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    resolved.push(next);
  });
  return resolved;
};

export const refreshPeersToCurrentYear = () => (dispatch, getState) => {
  const state = getState();
  const filename = state.persistence?.filename;
  const inception = programSelectors.getInception(state);
  const peers = state.peers?.submissions || [];

  if (!filename || !inception || !peers.length) {
    return Promise.resolve();
  }

  const submissionId = normalizeId(filename);
  const peerIds = peers
    .map((p) => (p.filename || "").replace(".json", ""))
    .filter(Boolean);
  const ids = [submissionId, ...new Set(peerIds)];

  return dispatch(
    submissionsService.submissionsApi.endpoints.listRenewalSubmissions.initiate(
      { ids },
      { forceRefetch: true }
    )
  )
    .unwrap()
    .then((renewalData) => {
      // The lookup was async. If another submission has been loaded (or is
      // loading) since the click, this result belongs to the old file and
      // must not replace the new one's peers or add rows to it.
      const now = getState();
      if (
        now.persistence?.filename !== filename ||
        now.persistence?.state === "LOADING"
      ) {
        return Promise.resolve();
      }

      // Resolve the peers as they are now, not as they were at the click, so
      // a peer added, deleted or re-ticked while the lookup ran is kept as
      // the UW left it. A peer added since has no renewal data and stays.
      const { renewalMap = {}, submissions: subs = {} } = renewalData ?? {};
      const resolved = resolvePeersToRenewals(
        now.peers?.submissions || [],
        renewalMap,
        subs,
        submissionId
      );

      // No auto-save: the swap leaves the submission dirty and the UW
      // reviews it, re-selects weightings and saves as usual.
      dispatch(replacePeerSubmissions(resolved));
      const loadPromises = resolved.map((p) => dispatch(loadPeer(p)));
      return Promise.allSettled(loadPromises);
    })
    .catch((error) => {
      logger.info("[refreshPeers] Error", error);
      dispatch(
        errorActions.handleError(error, {
          title: "Failed to refresh peers",
          message:
            "Could not refresh peers to the current year. Please try again.",
        })
      );
    });
};

export const deletePeer = (filename) => ({
  type: types.DELETE_PEER,
  payload: filename,
});

export const updatePeersNote = (note) => ({
  type: types.UPDATE_PEERS_NOTE,
  payload: note,
});

export const updateIndustryClass = (industryCategory) => (dispatch) => {
  dispatch({
    type: types.UPDATE_PEERS_INDUSTRY_CLASS,
    payload: industryCategory,
  });
};
